window.AASupabaseClient = (() => {
  let client = null;

  function init() {
    if (client) return client;

    if (!window.supabase) {
      console.error('Supabase library is not loaded.');
      return null;
    }

    const config = window.AA_SUPABASE_CONFIG;

    if (!config || !config.url || !config.anonKey) {
      console.warn('Supabase config is missing.');
      return null;
    }

    client = window.supabase.createClient(config.url, config.anonKey);
    return client;
  }

  function getClient() {
    return client || init();
  }

  function getPlaybookServiceMethod(methodName) {
    const service = window.AAPlaybookService;

    if (!service || typeof service[methodName] !== 'function') {
      throw new Error(
        `AAPlaybookService.${methodName}() is not available.`
      );
    }

    return service[methodName];
  }

  async function getClients() {
    return getPlaybookServiceMethod('getClients')();
  }

  async function createClient(payload = {}) {
  const result = await getPlaybookServiceMethod('createClient')({
    businessName:
      payload.businessName ||
      payload.business_name,
    clientCode:
      payload.clientCode ??
      payload.client_code
  });

  if (!result?.success || !result?.client) {
    throw result?.error || new Error('Unable to create client.');
  }

  return result.client;
}

async function updateClient(payload = {}) {
  const updates = payload.updates || payload;

  const result = await getPlaybookServiceMethod('updateClient')({
    id: payload.id || payload.clientId,
    businessName:
      updates.businessName ??
      updates.business_name,
    clientCode:
      updates.clientCode ??
      updates.client_code,
    activeStatus:
      updates.activeStatus ??
      updates.active_status,
    accountStatus: updates.accountStatus ?? updates.account_status,
    invoiceStatus: updates.invoiceStatus ?? updates.invoice_status
  });

  if (!result?.success) {
    throw result?.error || new Error('Unable to update client.');
  }

  return result.client;
}

async function supportsClientCodes() {
  const db = getClient();

  if (!db) {
    return false;
  }

  const { error } = await db
    .from('aa_clients')
    .select('client_code')
    .limit(1);

  return !error;
}

async function supportsClientAssignments() {
  const db = getClient();

  if (!db) {
    return false;
  }

  const clientCheck = await db
    .from('aa_clients')
    .select('primary_team_member_id')
    .limit(1);

  if (clientCheck.error) {
    return false;
  }

  const teamCheck = await db
    .from('aa_internal_team')
    .select('display_name')
    .limit(1);

  return !teamCheck.error;
}

async function getInternalTeamMembers() {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  const { data, error } = await db
    .from('aa_internal_team')
    .select(`
      id,
      user_id,
      display_name,
      is_active,
      aa_roles (
        role_key,
        display_name
      )
    `)
    .eq('is_active', true)
    .order('display_name', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
}

async function getClientPrimaryAssignments() {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  const { data, error } = await db
    .from('aa_clients')
    .select('id, primary_team_member_id');

  if (error) {
    throw error;
  }

  return data || [];
}

async function updateClientPrimaryTeamMember({
  clientId,
  teamMemberId
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!clientId) {
    throw new Error('Client id is required.');
  }

  const { data, error } = await db
    .from('aa_clients')
    .update({
      primary_team_member_id: teamMemberId || null,
      updated_at: new Date().toISOString()
    })
    .eq('id', clientId)
    .select('id, primary_team_member_id')
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function archiveClient(payload = {}) {
  const success = await getPlaybookServiceMethod('archiveClient')({
    id: payload.id || payload.clientId
  });

  if (!success) {
    throw new Error('Unable to archive client.');
  }

  return true;
}

  async function getClientConfiguration(payload) {
    return getPlaybookServiceMethod('getClientConfiguration')(payload);
  }

  async function saveClientConfiguration(payload) {
    return getPlaybookServiceMethod('saveClientConfiguration')(payload);
  }

  async function getClientAccount(payload) {
  return getPlaybookServiceMethod('getClientAccount')(payload);
}

async function deleteClientTemplateAssignment({
  assignmentId
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!assignmentId) {
    throw new Error('Template assignment id is required.');
  }

  const { data: assignment, error: lookupError } = await db
    .from('aa_client_template_assignments')
    .select('id, assignment_status')
    .eq('id', assignmentId)
    .single();

  if (lookupError) {
    throw lookupError;
  }

  const status = String(
    assignment?.assignment_status || ''
  )
    .trim()
    .toLowerCase();

  if (status === 'generated') {
    throw new Error(
      'This template is in use and cannot be deleted.'
    );
  }

  const { error } = await db
    .from('aa_client_template_assignments')
    .delete()
    .eq('id', assignmentId);

  if (error) {
    throw error;
  }

  return true;
}

async function updateClientTemplateAssignmentStatus({
  assignmentId,
  status
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!assignmentId) {
    throw new Error('Template assignment id is required.');
  }

  if (!status) {
    throw new Error('Template assignment status is required.');
  }

  const { data, error } = await db
    .from('aa_client_template_assignments')
    .update({
      assignment_status: status,
      updated_at: new Date().toISOString()
    })
    .eq('id', assignmentId)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}
function normalizeCatalogMonth(value) {
  if (!value) {
    throw new Error('Catalog month is required.');
  }

  const raw = String(value).trim();

  const monthMatch = raw.match(/^(\d{4})-(\d{2})/);

  if (!monthMatch) {
    throw new Error('Catalog month must be a valid year and month.');
  }

  return `${monthMatch[1]}-${monthMatch[2]}-01`;
}

async function uploadCatalogCover({
  clientId,
  file
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!clientId) {
    throw new Error('Client id is required.');
  }

  if (!file) {
    throw new Error('Catalog cover file is required.');
  }

  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/webp'
  ];

  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      'Catalog cover must be a JPG, PNG, or WebP image.'
    );
  }

  const maxFileSize = 10 * 1024 * 1024;

  if (file.size > maxFileSize) {
    throw new Error(
      'Catalog cover must be 10 MB or smaller.'
    );
  }

  const extension =
    file.name.split('.').pop()?.toLowerCase() || 'jpg';

  const safeClientId = String(clientId)
    .replace(/[^a-zA-Z0-9-]/g, '');

  const fileName =
    `${safeClientId}/${Date.now()}-${crypto.randomUUID()}.${extension}`;

  const { error: uploadError } = await db.storage
    .from('catalog-covers')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type
    });

  if (uploadError) {
    throw uploadError;
  }

  const { data } = db.storage
    .from('catalog-covers')
    .getPublicUrl(fileName);

  if (!data?.publicUrl) {
    throw new Error(
      'Catalog cover uploaded but no public URL was returned.'
    );
  }

  return {
    path: fileName,
    publicUrl: data.publicUrl
  };
}


async function uploadCatalogPdf({
  clientId,
  file
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!clientId) {
    throw new Error(
      'Client id is required.'
    );
  }

  if (!file) {
    throw new Error(
      'Catalog PDF is required.'
    );
  }

  if (file.type !== 'application/pdf') {
    throw new Error(
      'Catalog file must be a PDF.'
    );
  }

  const maxFileSize =
    75 * 1024 * 1024;

  if (file.size > maxFileSize) {
    throw new Error(
      'Catalog PDF must be 75 MB or smaller.'
    );
  }

  const safeClientId =
    String(clientId)
      .replace(
        /[^a-zA-Z0-9-]/g,
        ''
      );

  const fileName =
    `${safeClientId}/` +
    `${Date.now()}-${crypto.randomUUID()}.pdf`;

  const { error: uploadError } =
    await db.storage
      .from('catalog-files')
      .upload(
        fileName,
        file,
        {
          cacheControl: '3600',
          upsert: false,
          contentType:
            'application/pdf'
        }
      );

  if (uploadError) {
    throw uploadError;
  }

  return {
    path: fileName,
    fileName: file.name,
    fileSize: file.size
  };
}

function getCatalogPdfPublicUrl({
  path
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!path) {
    return '';
  }

  const { data } =
    db.storage
      .from('catalog-files')
      .getPublicUrl(path);

  return data?.publicUrl || '';
}

async function getCatalogPdfSignedUrl({
  path,
  expiresIn = 3600
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!path) {
    throw new Error(
      'Catalog PDF path is required.'
    );
  }

  const { data, error } =
    await db.storage
      .from('catalog-files')
      .createSignedUrl(
        path,
        expiresIn
      );

  if (error) {
    throw error;
  }

  if (!data?.signedUrl) {
    throw new Error(
      'Unable to create catalog PDF access URL.'
    );
  }

  return data.signedUrl;
}

async function deleteCatalogPdf({
  path
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!path) {
    return true;
  }

  const { error } =
    await db.storage
      .from('catalog-files')
      .remove([path]);

  if (error) {
    throw error;
  }

  return true;
}

async function uploadPlaybookFile({
  clientId,
  playbookId,
  fieldId,
  file
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!clientId) {
    throw new Error(
      'Client id is required.'
    );
  }

  if (!playbookId) {
    throw new Error(
      'Playbook id is required.'
    );
  }

  if (!fieldId) {
    throw new Error(
      'Field id is required.'
    );
  }

  if (!file) {
    throw new Error(
      'Playbook file is required.'
    );
  }

  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/pdf'
  ];

  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      'File must be a JPG, PNG, WebP, or PDF.'
    );
  }

  const maxFileSize =
    20 * 1024 * 1024;

  if (file.size > maxFileSize) {
    throw new Error(
      'File must be 20 MB or smaller.'
    );
  }

  const extension =
    file.name
      .split('.')
      .pop()
      ?.toLowerCase() ||
    'file';

  const safeClientId =
    String(clientId)
      .replace(
        /[^a-zA-Z0-9-]/g,
        ''
      );

  const safePlaybookId =
    String(playbookId)
      .replace(
        /[^a-zA-Z0-9-]/g,
        ''
      );

  const safeFieldId =
    String(fieldId)
      .replace(
        /[^a-zA-Z0-9_-]/g,
        '-'
      );

  const fileName =
    `${safeClientId}/` +
    `${safePlaybookId}/` +
    `${safeFieldId}/` +
    `${Date.now()}-${crypto.randomUUID()}.${extension}`;

  const { error: uploadError } =
    await db.storage
      .from('playbook-files')
      .upload(
        fileName,
        file,
        {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        }
      );

  if (uploadError) {
    throw uploadError;
  }

  return {
    path: fileName,
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size
  };
}

async function getPlaybookFileSignedUrl({
  path,
  expiresIn = 3600
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!path) {
    throw new Error(
      'Playbook file path is required.'
    );
  }

  const {
    data,
    error
  } = await db.storage
    .from('playbook-files')
    .createSignedUrl(
      path,
      expiresIn
    );

  if (error) {
    throw error;
  }

  if (!data?.signedUrl) {
    throw new Error(
      'Unable to create file access URL.'
    );
  }

  return data.signedUrl;
}

async function deletePlaybookFile({
  path
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!path) {
    throw new Error(
      'Playbook file path is required.'
    );
  }

  const { error } =
    await db.storage
      .from('playbook-files')
      .remove([path]);

  if (error) {
    throw error;
  }

  return true;
}

async function getCatalogRecords({
  clientId,
  year,
  startDate,
  endDate,
  brand,
  status = 'active'
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!clientId) {
    throw new Error('Client id is required.');
  }

  let query = db
    .from('aa_catalog_records')
    .select('*')
    .eq('client_id', clientId)
    .eq('status', status === 'archived' ? 'archived' : 'active')
    .order('catalog_month', { ascending: false });

  if (year) {
    const numericYear = Number(year);

    if (!Number.isInteger(numericYear)) {
      throw new Error('Catalog year must be valid.');
    }

    query = query
      .gte('catalog_month', `${numericYear}-01-01`)
      .lte('catalog_month', `${numericYear}-12-01`);
  }

  if (startDate) {
    query = query.gte(
      'catalog_month',
      normalizeCatalogMonth(startDate)
    );
  }

 if (endDate) {
  query = query.lte(
    'catalog_month',
    normalizeCatalogMonth(endDate)
  );
}

if (brand) {
  query = query.filter(
    'brands',
    'cs',
    JSON.stringify([
      String(brand).trim()
    ])
  );
}

const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data || [];
}

async function getCatalogRecord({ id } = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!id) {
    throw new Error('Catalog record id is required.');
  }

  const { data, error } = await db
    .from('aa_catalog_records')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function createCatalogRecord(payload = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!payload.client_id && !payload.clientId) {
    throw new Error('Client id is required.');
  }

  if (!payload.catalog_month && !payload.catalogMonth) {
    throw new Error('Catalog month is required.');
  }

  const clientId =
    payload.client_id ||
    payload.clientId;

  const catalogMonth = normalizeCatalogMonth(
    payload.catalog_month ||
    payload.catalogMonth
  );

  const { data: existingRecords, error: duplicateError } =
    await db
      .from('aa_catalog_records')
      .select('id, catalog_title, catalog_month')
      .eq('client_id', clientId)
      .eq('catalog_month', catalogMonth)
      .eq('status', 'active')
      .limit(1);

  if (duplicateError) {
    throw duplicateError;
  }

  if (existingRecords?.length) {
    const duplicate = existingRecords[0];

    const error = new Error(
      'An active catalog already exists for this client and month. Edit the existing catalog instead.'
    );

    error.code = 'AA_DUPLICATE_CATALOG_MONTH';
    error.catalogRecord = duplicate;

    throw error;
  }

  const record = {
    client_id: clientId,
    catalog_month: catalogMonth,
    publication_date:
      payload.publication_date ??
      payload.publicationDate ??
      null,
    catalog_title:
      payload.catalog_title ??
      payload.catalogTitle ??
      '',
    catalog_url:
      payload.catalog_url ??
      payload.catalogUrl ??
      '',
    cover_image_url:
  payload.cover_image_url ??
  payload.coverImageUrl ??
  null,
heyzine_flipbook_id:
  payload.heyzine_flipbook_id ??
  payload.heyzineFlipbookId ??
  null,
catalog_pdf_storage_path:
  payload.catalog_pdf_storage_path ??
  payload.catalogPdfStoragePath ??
  null,
catalog_pdf_file_name:
  payload.catalog_pdf_file_name ??
  payload.catalogPdfFileName ??
  null,
catalog_pdf_file_size:
  payload.catalog_pdf_file_size ??
  payload.catalogPdfFileSize ??
  null,
catalog_pdf_uploaded_at:
  payload.catalog_pdf_storage_path ||
  payload.catalogPdfStoragePath
    ? new Date().toISOString()
    : null,
catalog_views:
      payload.catalog_views ??
      payload.catalogViews ??
      null,
    brands: Array.isArray(payload.brands)
      ? payload.brands
      : [],
    status: 'active',
    updated_at: new Date().toISOString()
  };

  const { data, error } = await db
    .from('aa_catalog_records')
    .insert(record)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function updateCatalogRecord({
  id,
  updates = {}
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!id) {
    throw new Error('Catalog record id is required.');
  }

  const payload = {
    ...updates,
    updated_at: new Date().toISOString()
  };

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      'catalogMonth'
    )
  ) {
    payload.catalog_month = normalizeCatalogMonth(
      payload.catalogMonth
    );

    delete payload.catalogMonth;
  }

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      'catalog_month'
    )
  ) {
    payload.catalog_month = normalizeCatalogMonth(
      payload.catalog_month
    );
  }

  const camelCaseFields = {
    publicationDate: 'publication_date',
    catalogTitle: 'catalog_title',
    catalogUrl: 'catalog_url',
   coverImageUrl: 'cover_image_url',
heyzineFlipbookId: 'heyzine_flipbook_id',
catalogPdfStoragePath:
  'catalog_pdf_storage_path',
catalogPdfFileName:
  'catalog_pdf_file_name',
catalogPdfFileSize:
  'catalog_pdf_file_size',
catalogPdfUploadedAt:
  'catalog_pdf_uploaded_at',
catalogViews: 'catalog_views'
  };

  Object.entries(camelCaseFields).forEach(
    ([source, destination]) => {
      if (
        Object.prototype.hasOwnProperty.call(
          payload,
          source
        )
      ) {
        payload[destination] = payload[source];
        delete payload[source];
      }
    }
  );

  const { data, error } = await db
    .from('aa_catalog_records')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function archiveCatalogRecord({ id } = {}) {
  return updateCatalogRecord({
    id,
    updates: {
      status: 'archived'
    }
  });
}



async function canPermanentlyDeleteCatalogs() {
  const db = getClient();
  if (!db) return false;
  const { data, error } = await db.rpc('aa_is_internal_admin');
  if (error) {
    console.warn('Unable to check catalog deletion permission:', error);
    return false;
  }
  return data === true;
}

async function previewCatalogDeletion({ clientId, catalogIds } = {}) {
  const db = getClient();
  if (!db) throw new Error('Supabase client is unavailable.');
  const { data, error } = await db.rpc('aa_manage_catalog_deletion', {
    p_client_id: clientId,
    p_catalog_ids: catalogIds,
    p_delete: false
  });
  if (error) throw error;
  return data;
}

async function permanentlyDeleteCatalogs({ clientId, catalogIds } = {}) {
  const db = getClient();
  if (!db) throw new Error('Supabase client is unavailable.');
  const { data, error } = await db.rpc('aa_manage_catalog_deletion', {
    p_client_id: clientId,
    p_catalog_ids: catalogIds,
    p_delete: true
  });
  if (error) throw error;

  // Database deletion is atomic. Object storage cleanup follows commit.
  // Never remove externally hosted covers or files shared by surviving catalogs.
  const pending = [];
  const existingFiles = new Set();
  const seen = new Set();
  for (const catalog of data?.catalogs || []) {
    if (catalog.pdfPath) {
      const path = String(catalog.pdfPath);
      const key = 'catalog-files:' + path;
      if (!seen.has(key)) {
        seen.add(key);
        pending.push({ bucket: 'catalog-files', path });
      }
    }
    if (catalog.coverUrl) {
      try {
        const url = new URL(catalog.coverUrl);
        const expectedOrigin = new URL(window.AA_SUPABASE_CONFIG?.url).origin;
        if (url.origin !== expectedOrigin) continue;
        const marker = '/storage/v1/object/public/catalog-covers/';
        const index = url.pathname.indexOf(marker);
        if (index >= 0) {
          const path = decodeURIComponent(url.pathname.slice(index + marker.length));
          const key = 'catalog-covers:' + path;
          if (path && !seen.has(key)) {
            seen.add(key);
            pending.push({ bucket: 'catalog-covers', path });
          }
        }
      } catch (_) {
        // External or malformed cover URLs are never deleted.
      }
    }
  }
  const failures = [];
  for (const file of pending) {
    try {
    const { data: references, error: referenceError } = await db
      .from('aa_catalog_records')
      .select('id')
      .eq(file.bucket === 'catalog-files' ? 'catalog_pdf_storage_path' : 'cover_image_url',
        file.bucket === 'catalog-files' ? file.path : (
          (data.catalogs || []).find(c => {
            try {
              return c.coverUrl && decodeURIComponent(new URL(c.coverUrl).pathname)
                .endsWith('/catalog-covers/' + file.path);
            } catch (_) { return false; }
          })?.coverUrl || ''
        ))
      .limit(1);
    if (referenceError) {
      failures.push(file);
      continue;
    }
    if ((references || []).length) {
      // A surviving catalog still references the object.
      continue;
    }
    const { error: removeError } = await db.storage.from(file.bucket).remove([file.path]);
    if (removeError) failures.push(file);
    } catch (cleanupError) {
      // The database deletion already committed. Report cleanup failures
      // without incorrectly reporting the catalog deletion itself as failed.
      console.warn('Catalog storage cleanup needs attention:', cleanupError);
      failures.push(file);
    }
  }
  return { ...data, cleanupFailures: failures, cleanupAttempted: pending.length };
}

async function getCoopReports({
  clientId,
  includeArchived = false
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!clientId) {
    throw new Error('Client id is required.');
  }

  let query = db
    .from('aa_coop_reports')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });

  if (!includeArchived) {
    query = query.neq(
      'status',
      'archived'
    );
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return (data || []).map((item) => ({
    id: item.id,
    clientId: item.client_id,
    reportTitle: item.report_title || '',
    dateStart: item.date_start || '',
    dateEnd: item.date_end || '',
    manufacturer: item.manufacturer || '',
    catalogIds: Array.isArray(item.catalog_ids)
      ? item.catalog_ids
      : [],
    catalogSnapshot: Array.isArray(item.catalog_snapshot)
      ? item.catalog_snapshot
      : [],
    catalogCount: Number(item.catalog_count || 0),
    totalViews:
      item.total_views === null ||
      item.total_views === undefined
        ? null
        : Number(item.total_views),
    status: item.status || 'generated',
    pdfStoragePath: item.pdf_storage_path || '',
    createdAt: item.created_at || '',
    updatedAt: item.updated_at || ''
  }));
}

async function createCoopReport(
  payload = {}
) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  const clientId =
    payload.clientId ||
    payload.client_id;

  const reportTitle =
    String(
      payload.reportTitle ||
      payload.report_title ||
      ''
    ).trim();

  const dateStart =
    payload.dateStart ||
    payload.date_start;

  const dateEnd =
    payload.dateEnd ||
    payload.date_end;

  const catalogSnapshot =
    Array.isArray(payload.catalogSnapshot)
      ? payload.catalogSnapshot
      : Array.isArray(payload.catalog_snapshot)
        ? payload.catalog_snapshot
        : [];

  if (!clientId) {
    throw new Error('Client id is required.');
  }

  if (!reportTitle) {
    throw new Error('Report title is required.');
  }

  if (!dateStart || !dateEnd) {
    throw new Error('Report start and end dates are required.');
  }

  if (!catalogSnapshot.length) {
    throw new Error(
      'At least one catalog is required for a Co-op report.'
    );
  }

  const catalogIds =
    catalogSnapshot
      .map((catalog) => catalog.id)
      .filter(Boolean);

  const views =
    catalogSnapshot
      .map((catalog) =>
        Number(catalog.catalog_views)
      )
      .filter(Number.isFinite);

  const totalViews =
    views.length
      ? views.reduce(
          (sum, value) => sum + value,
          0
        )
      : null;

  const { data, error } = await db
    .from('aa_coop_reports')
    .insert({
      client_id: clientId,
      report_title: reportTitle,
      date_start: dateStart,
      date_end: dateEnd,
      manufacturer:
        String(
          payload.manufacturer || ''
        ).trim() || null,
      catalog_ids: catalogIds,
      catalog_snapshot: catalogSnapshot,
      catalog_count:
        catalogSnapshot.length,
      total_views: totalViews,
      status: 'generated',
      updated_at:
        new Date().toISOString()
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  return {
    id: data.id,
    clientId: data.client_id,
    reportTitle: data.report_title || '',
    dateStart: data.date_start || '',
    dateEnd: data.date_end || '',
    manufacturer: data.manufacturer || '',
    catalogIds: Array.isArray(data.catalog_ids)
      ? data.catalog_ids
      : [],
    catalogSnapshot: Array.isArray(data.catalog_snapshot)
      ? data.catalog_snapshot
      : [],
    catalogCount: Number(data.catalog_count || 0),
    totalViews:
      data.total_views === null ||
      data.total_views === undefined
        ? null
        : Number(data.total_views),
    status: data.status || 'generated',
    pdfStoragePath: data.pdf_storage_path || '',
    createdAt: data.created_at || '',
    updatedAt: data.updated_at || ''
  };
}

async function archiveCoopReport({
  id
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!id) {
    throw new Error(
      'Co-op report id is required.'
    );
  }

  const { data, error } = await db
    .from('aa_coop_reports')
    .update({
      status: 'archived',
      updated_at:
        new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function deleteCoopReport({
  id
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Co-op report id is required.'
    );
  }

  const { error } =
    await db
      .from('aa_coop_reports')
      .delete()
      .eq('id', id);

  if (error) {
    throw error;
  }

  return true;
}


async function getPreviousMonthCatalog({
  clientId,
  referenceMonth
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!clientId) {
    throw new Error('Client id is required.');
  }

  if (!referenceMonth) {
    throw new Error('Reference month is required.');
  }

  const normalizedReferenceMonth =
    normalizeCatalogMonth(referenceMonth);

  const [year, month] =
    normalizedReferenceMonth
      .split('-')
      .map(Number);

  const previousDate = new Date(
    Date.UTC(year, month - 2, 1)
  );

  const previousMonth =
    `${previousDate.getUTCFullYear()}-` +
    `${String(
      previousDate.getUTCMonth() + 1
    ).padStart(2, '0')}-01`;

  const { data, error } = await db
    .from('aa_catalog_records')
    .select(`
      id,
      client_id,
      catalog_month,
      publication_date,
      catalog_title,
      catalog_url,
      cover_image_url,
heyzine_flipbook_id,
catalog_views,
      brands,
      status
    `)
    .eq('client_id', clientId)
    .eq('catalog_month', previousMonth)
    .eq('status', 'active')
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data || null;
}


function normalizeEmailRequestStatus(status) {
  const normalized =
    String(status || 'draft')
      .trim()
      .toLowerCase();

  const allowedStatuses = [
    'draft',
    'submitted',
    'in_progress',
    'ready',
    'sent',
    'archived'
  ];

  if (!allowedStatuses.includes(normalized)) {
    throw new Error(
      'Email request status is invalid.'
    );
  }

  return normalized;
}

async function getEmailRequests({
  clientId
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!clientId) {
    throw new Error(
      'Client id is required.'
    );
  }

  const { data, error } =
    await db
      .from('aa_email_requests')
      .select('*')
      .eq('client_id', clientId)
      .order(
        'email_date',
        { ascending: true }
      )
      .order(
        'created_at',
        { ascending: true }
      );

  if (error) {
    throw error;
  }

  return data || [];
}

async function getEmailRequest({
  id
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Email request id is required.'
    );
  }

  const { data, error } =
    await db
      .from('aa_email_requests')
      .select('*')
      .eq('id', id)
      .maybeSingle();

  if (error) {
    throw error;
  }

  return data || null;
}

async function createEmailRequest(
  payload = {}
) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const clientId =
    payload.clientId ||
    payload.client_id;

  const emailDate =
    payload.emailDate ||
    payload.email_date;

  const emailTopic =
    payload.emailTopic ||
    payload.email_topic ||
    payload.topic;

  if (!clientId) {
    throw new Error(
      'Client id is required.'
    );
  }

  if (!emailDate) {
    throw new Error(
      'Email date is required.'
    );
  }

  if (
    !emailTopic ||
    !String(emailTopic).trim()
  ) {
    throw new Error(
      'Email topic is required.'
    );
  }

  const status =
    normalizeEmailRequestStatus(
      payload.status || 'draft'
    );

  const record = {
    client_id: clientId,
    email_date: emailDate,
    email_topic:
      String(emailTopic).trim(),
    subject:
      payload.subject === undefined ||
      payload.subject === null ||
      String(payload.subject).trim() === ''
        ? null
        : String(payload.subject).trim(),
    preheader:
      payload.preheader === undefined ||
      payload.preheader === null ||
      String(payload.preheader).trim() === ''
        ? null
        : String(payload.preheader).trim(),
    notes:
      payload.notes === undefined ||
      payload.notes === null ||
      String(payload.notes).trim() === ''
        ? null
        : String(payload.notes).trim(),
    status,
    updated_at:
      new Date().toISOString()
  };

  const { data, error } =
    await db
      .from('aa_email_requests')
      .insert(record)
      .select()
      .single();

  if (error) {
    throw error;
  }

  return data;
}

async function updateEmailRequest({
  id,
  updates = {}
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Email request id is required.'
    );
  }

  const payload = {};

  if (
    Object.prototype.hasOwnProperty.call(
      updates,
      'emailDate'
    )
  ) {
    payload.email_date =
      updates.emailDate;
  }

  if (
    Object.prototype.hasOwnProperty.call(
      updates,
      'email_date'
    )
  ) {
    payload.email_date =
      updates.email_date;
  }

  if (
    Object.prototype.hasOwnProperty.call(
      updates,
      'emailTopic'
    )
  ) {
    payload.email_topic =
      String(
        updates.emailTopic || ''
      ).trim();
  }

  if (
    Object.prototype.hasOwnProperty.call(
      updates,
      'email_topic'
    )
  ) {
    payload.email_topic =
      String(
        updates.email_topic || ''
      ).trim();
  }

  if (
    Object.prototype.hasOwnProperty.call(
      updates,
      'topic'
    )
  ) {
    payload.email_topic =
      String(
        updates.topic || ''
      ).trim();
  }

  [
    'subject',
    'preheader',
    'notes'
  ].forEach((field) => {
    if (
      Object.prototype.hasOwnProperty.call(
        updates,
        field
      )
    ) {
      const value =
        updates[field];

      payload[field] =
        value === undefined ||
        value === null ||
        String(value).trim() === ''
          ? null
          : String(value).trim();
    }
  });

  if (
    Object.prototype.hasOwnProperty.call(
      updates,
      'status'
    )
  ) {
    payload.status =
      normalizeEmailRequestStatus(
        updates.status
      );
  }

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      'email_topic'
    ) &&
    !payload.email_topic
  ) {
    throw new Error(
      'Email topic is required.'
    );
  }

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      'email_date'
    ) &&
    !payload.email_date
  ) {
    throw new Error(
      'Email date is required.'
    );
  }

  payload.updated_at =
    new Date().toISOString();

  const { data, error } =
    await db
      .from('aa_email_requests')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

  if (error) {
    throw error;
  }

  return data;
}

async function archiveEmailRequest({
  id
} = {}) {
  return updateEmailRequest({
    id,
    updates: {
      status: 'archived'
    }
  });
}

async function deleteEmailRequest({
  id
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Email request id is required.'
    );
  }

  const files =
    await getEmailRequestFiles({
      requestId: id
    });

  for (const file of files) {
    await removeEmailRequestFile({
      id: file.id
    });
  }

  const { data, error } = await db.rpc(
    'aa_delete_email_request',
    {
      p_request_id: id
    }
  );

  if (error) {
    throw error;
  }

  return data || {
    success: true
  };
}

async function getEmailRequestFiles({
  requestId
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!requestId) {
    throw new Error(
      'Email request id is required.'
    );
  }

  const { data, error } =
    await db
      .from(
        'aa_email_request_files'
      )
      .select('*')
      .eq(
        'email_request_id',
        requestId
      )
      .order(
        'created_at',
        { ascending: true }
      );

  if (error) {
    throw error;
  }

  return data || [];
}

async function uploadEmailRequestFile({
  clientId,
  requestId,
  file
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!clientId) {
    throw new Error(
      'Client id is required.'
    );
  }

  if (!requestId) {
    throw new Error(
      'Email request id is required.'
    );
  }

  if (!file) {
    throw new Error(
      'Email request file is required.'
    );
  }

  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/pdf'
  ];

  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      'File must be a JPG, PNG, WebP, or PDF.'
    );
  }

  const maxFileSize =
    20 * 1024 * 1024;

  if (file.size > maxFileSize) {
    throw new Error(
      'File must be 20 MB or smaller.'
    );
  }

  const extension =
    file.name
      .split('.')
      .pop()
      ?.toLowerCase() ||
    'file';

  const safeClientId =
    String(clientId)
      .replace(
        /[^a-zA-Z0-9-]/g,
        ''
      );

  const safeRequestId =
    String(requestId)
      .replace(
        /[^a-zA-Z0-9-]/g,
        ''
      );

  const storagePath =
    `${safeClientId}/` +
    `${safeRequestId}/` +
    `${Date.now()}-` +
    `${crypto.randomUUID()}.` +
    `${extension}`;

  const { error: uploadError } =
    await db.storage
      .from(
        'email-request-files'
      )
      .upload(
        storagePath,
        file,
        {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        }
      );

  if (uploadError) {
    throw uploadError;
  }

  const {
    data,
    error: recordError
  } =
    await db
      .from(
        'aa_email_request_files'
      )
      .insert({
        email_request_id:
          requestId,
        client_id:
          clientId,
        file_name:
          file.name,
        storage_path:
          storagePath,
        file_type:
          file.type
      })
      .select()
      .single();

  if (recordError) {
    try {
      await db.storage
        .from(
          'email-request-files'
        )
        .remove([
          storagePath
        ]);
    } catch (
      cleanupError
    ) {
      console.error(
        'Unable to clean up failed Email file upload:',
        cleanupError
      );
    }

    throw recordError;
  }

  return data;
}

async function getEmailRequestFileSignedUrl({
  path,
  expiresIn = 3600
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!path) {
    throw new Error(
      'Email request file path is required.'
    );
  }

  const {
    data,
    error
  } =
    await db.storage
      .from(
        'email-request-files'
      )
      .createSignedUrl(
        path,
        expiresIn
      );

  if (error) {
    throw error;
  }

  if (!data?.signedUrl) {
    throw new Error(
      'Unable to create file access URL.'
    );
  }

  return data.signedUrl;
}

async function removeEmailRequestFile({
  id
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Email request file id is required.'
    );
  }

  const {
    data: fileRecord,
    error: lookupError
  } =
    await db
      .from(
        'aa_email_request_files'
      )
      .select(
        'id, storage_path'
      )
      .eq('id', id)
      .single();

  if (lookupError) {
    throw lookupError;
  }

  const { error: storageError } =
    await db.storage
      .from(
        'email-request-files'
      )
      .remove([
        fileRecord.storage_path
      ]);

  if (storageError) {
    throw storageError;
  }

  const { error: deleteError } =
    await db
      .from(
        'aa_email_request_files'
      )
      .delete()
      .eq('id', id);

  if (deleteError) {
    throw deleteError;
  }

  return true;
}

async function supportsTasks() {
  const db = getClient();

  if (!db) {
    return false;
  }

  const { error } = await db
    .from('aa_tasks')
    .select('id')
    .limit(1);

  return !error;
}

async function getClientProductionSettings({
  clientId
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!clientId) {
    return null;
  }

  const { data, error } = await db
    .from('aa_client_production_settings')
    .select('*')
    .eq('client_id', clientId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    return {
      clientId,
      preferredEmailWeekday: '',
      preferredEmailWeekdays: [],
      preferredEmailTime: '',
      doNotPromote: '',
      priorityPromotions: '',
      standingRules: ''
    };
  }

  return {
    clientId: data.client_id,
    preferredEmailWeekday:
      data.preferred_email_weekday || '',
    preferredEmailWeekdays:
      Array.isArray(data.preferred_email_weekdays) && data.preferred_email_weekdays.length
        ? data.preferred_email_weekdays
        : (data.preferred_email_weekday ? [data.preferred_email_weekday] : []),
    preferredEmailTime:
      data.preferred_email_time
        ? String(data.preferred_email_time)
            .slice(0, 5)
        : '',
    doNotPromote:
      data.do_not_promote || '',
    priorityPromotions:
      data.priority_promotions || '',
    standingRules:
      data.standing_rules || ''
  };
}

async function saveClientProductionSettings({
  clientId,
  preferredEmailWeekday = '',
  preferredEmailWeekdays = null,
  preferredEmailTime = '',
  doNotPromote = '',
  priorityPromotions = '',
  standingRules = ''
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!clientId) {
    throw new Error(
      'Client is required.'
    );
  }

  const validDays = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
  const requestedDays = Array.isArray(preferredEmailWeekdays)
    ? preferredEmailWeekdays
    : (preferredEmailWeekday ? [preferredEmailWeekday] : []);
  const normalizedEmailDays = validDays.filter(day => requestedDays.includes(day));
  if (requestedDays.some(day => !validDays.includes(day))) {
    throw new Error('Choose valid email weekdays.');
  }

  const { data, error } = await db
    .from('aa_client_production_settings')
    .upsert(
      {
        client_id: clientId,
        preferred_email_weekday:
          normalizedEmailDays[0] || null,
        preferred_email_weekdays: normalizedEmailDays,
        preferred_email_time:
          preferredEmailTime || null,
        do_not_promote:
          String(doNotPromote || '').trim(),
        priority_promotions:
          String(priorityPromotions || '').trim(),
        standing_rules:
          String(standingRules || '').trim(),
        updated_at:
          new Date().toISOString()
      },
      {
        onConflict: 'client_id'
      }
    )
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function getClientTaskAnchors({
  clientId,
  includeInactive = true
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!clientId) {
    return [];
  }

  let query = db
    .from('aa_client_task_anchors')
    .select('*')
    .eq('client_id', clientId)
    .order('is_active', {
      ascending: false
    })
    .order('created_at', {
      ascending: true
    });

  if (!includeInactive) {
    query = query.eq(
      'is_active',
      true
    );
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return (data || []).map(
    (item) => ({
      id: item.id,
      clientId: item.client_id,
      taskName: item.task_name || '',
      taskType: item.task_type || '',
      scheduleKind:
        item.schedule_kind ||
        'day_of_month',
      dayOfMonth:
        item.day_of_month ?? null,
      monthOfYear:
        item.month_of_year ?? null,
      weekOfMonth:
        item.week_of_month || '',
      weekday:
        item.weekday || '',
      useClientOwner:
        item.use_client_owner !== false,
      assigneeTeamMemberId:
        item.assignee_team_member_id || '',
      countsTowardPackage:
        item.counts_toward_package !== false,
      packageServiceId:
        item.package_service_id || '',
      packageUnits:
        Number(item.package_units || 1),
      notes:
        item.notes || '',
      isActive:
        item.is_active !== false,
      createdAt:
        item.created_at || '',
      updatedAt:
        item.updated_at || ''
    })
  );
}

async function createClientTaskAnchor(
  payload = {}
) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const clientId =
    payload.clientId ||
    payload.client_id;

  const taskName =
    String(
      payload.taskName ||
      payload.task_name ||
      ''
    ).trim();

  const taskType =
    String(
      payload.taskType ||
      payload.task_type ||
      ''
    ).trim();

  if (
    !clientId ||
    !taskName ||
    !taskType
  ) {
    throw new Error(
      'Client, task name, and task type are required.'
    );
  }

  const scheduleKind =
    payload.scheduleKind ||
    payload.schedule_kind ||
    'day_of_month';

  const { data, error } = await db
    .from('aa_client_task_anchors')
    .insert({
      client_id: clientId,
      task_name: taskName,
      task_type: taskType,
      schedule_kind: scheduleKind,
      day_of_month:
        ['day_of_month', 'yearly', 'quarterly'].includes(scheduleKind)
          ? Number(
              payload.dayOfMonth ||
              payload.day_of_month ||
              1
            )
          : null,
      month_of_year:
        ['yearly', 'yearly_flexible', 'quarterly', 'quarterly_flexible'].includes(scheduleKind)
          ? Number(payload.monthOfYear ?? payload.month_of_year ?? 1)
          : null,
      week_of_month:
        scheduleKind === 'nth_weekday'
          ? (
              payload.weekOfMonth ||
              payload.week_of_month ||
              'first'
            )
          : null,
      weekday:
        (
          scheduleKind === 'nth_weekday' ||
          scheduleKind === 'weekly'
        )
          ? (
              payload.weekday ||
              'Monday'
            )
          : null,
      use_client_owner:
        payload.useClientOwner !== false,
      assignee_team_member_id:
        payload.useClientOwner === false
          ? (
              payload.assigneeTeamMemberId ||
              payload.assignee_team_member_id ||
              null
            )
          : null,
      counts_toward_package:
        payload.countsTowardPackage !== false,
      package_service_id:
        payload.countsTowardPackage === false
          ? null
          : (
              payload.packageServiceId ||
              payload.package_service_id ||
              null
            ),
      package_units:
        Number(
          payload.packageUnits ||
          payload.package_units ||
          1
        ),
      notes:
        String(payload.notes || '').trim(),
      is_active:
        payload.isActive !== false,
      updated_at:
        new Date().toISOString()
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function updateClientTaskAnchor({
  id,
  updates = {}
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Task Anchor is required.'
    );
  }

  const payload = {
    updated_at:
      new Date().toISOString()
  };

  const map = {
    taskName: 'task_name',
    taskType: 'task_type',
    scheduleKind: 'schedule_kind',
    dayOfMonth: 'day_of_month',
    monthOfYear: 'month_of_year',
    weekOfMonth: 'week_of_month',
    weekday: 'weekday',
    useClientOwner: 'use_client_owner',
    assigneeTeamMemberId:
      'assignee_team_member_id',
    countsTowardPackage:
      'counts_toward_package',
    packageServiceId:
      'package_service_id',
    packageUnits:
      'package_units',
    notes: 'notes',
    isActive: 'is_active'
  };

  Object.entries(map).forEach(
    ([source, destination]) => {
      if (
        Object.prototype
          .hasOwnProperty
          .call(updates, source)
      ) {
        payload[destination] =
          updates[source];
      }
    }
  );

  const scheduleKind =
    updates.scheduleKind;

  if (scheduleKind === 'day_of_month') {
    payload.week_of_month = null;
    payload.weekday = null;
  }

  if (scheduleKind === 'nth_weekday') {
    payload.day_of_month = null;
  }

  if (scheduleKind === 'weekly') {
    payload.day_of_month = null;
    payload.week_of_month = null;
  }

  if (scheduleKind === 'monthly_flexible') {
    payload.day_of_month = null;
    payload.week_of_month = null;
    payload.weekday = null;
  }

  if (updates.scheduleKind && !['yearly', 'yearly_flexible', 'quarterly', 'quarterly_flexible'].includes(updates.scheduleKind)) {
    payload.month_of_year = null;
  }
  if (['yearly', 'yearly_flexible', 'quarterly', 'quarterly_flexible'].includes(updates.scheduleKind)) {
    payload.week_of_month = null;
    payload.weekday = null;
    if (updates.scheduleKind.endsWith('_flexible')) payload.day_of_month = null;
  }

  if (
    updates.useClientOwner === true
  ) {
    payload.assignee_team_member_id =
      null;
  }

  if (
    updates.countsTowardPackage === false
  ) {
    payload.package_service_id = null;
  }

  const { data, error } = await db
    .from('aa_client_task_anchors')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function deleteClientTaskAnchor({
  id
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Task Anchor is required.'
    );
  }

  const { error } = await db
    .from('aa_client_task_anchors')
    .delete()
    .eq('id', id);

  if (error) {
    throw error;
  }

  return true;
}


async function getSharedTaskAnchors({
  includeInactive = true
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  let anchorQuery = db
    .from('aa_shared_task_anchors')
    .select('*')
    .order('is_active', {
      ascending: false
    })
    .order('created_at', {
      ascending: true
    });

  if (!includeInactive) {
    anchorQuery = anchorQuery.eq(
      'is_active',
      true
    );
  }

  const [
    anchorResult,
    assignmentResult
  ] = await Promise.all([
    anchorQuery,
    db
      .from('aa_shared_task_anchor_clients')
      .select('shared_anchor_id, client_id')
  ]);

  if (anchorResult.error) {
    throw anchorResult.error;
  }

  if (assignmentResult.error) {
    throw assignmentResult.error;
  }

  const assignmentsByAnchor =
    (assignmentResult.data || [])
      .reduce((map, item) => {
        const key =
          String(item.shared_anchor_id);

        if (!map[key]) {
          map[key] = [];
        }

        map[key].push(item.client_id);

        return map;
      }, {});

  return (anchorResult.data || [])
    .map((item) => ({
      id: item.id,
      taskName: item.task_name || '',
      taskType: item.task_type || '',
      scheduleKind:
        item.schedule_kind ||
        'day_of_month',
      dayOfMonth:
        item.day_of_month ?? null,
      monthOfYear:
        item.month_of_year ?? null,
      weekOfMonth:
        item.week_of_month || '',
      weekday:
        item.weekday || '',
      useClientOwner:
        item.use_client_owner !== false,
      assigneeTeamMemberId:
        item.assignee_team_member_id || '',
      countsTowardPackage:
        item.counts_toward_package !== false,
      packageServiceCatalogId:
        item.package_service_catalog_id || '',
      notes:
        item.notes || '',
      isActive:
        item.is_active !== false,
      clientIds:
        assignmentsByAnchor[
          String(item.id)
        ] || [],
      createdAt:
        item.created_at || '',
      updatedAt:
        item.updated_at || ''
    }));
}

async function createSharedTaskAnchor(
  payload = {}
) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const taskName =
    String(
      payload.taskName ||
      payload.task_name ||
      ''
    ).trim();

  const taskType =
    String(
      payload.taskType ||
      payload.task_type ||
      ''
    ).trim();

  const clientIds =
    Array.isArray(payload.clientIds)
      ? [...new Set(
          payload.clientIds
            .map(String)
            .filter(Boolean)
        )]
      : [];

  if (!taskName || !taskType) {
    throw new Error(
      'Task name and task type are required.'
    );
  }

  if (!clientIds.length) {
    throw new Error(
      'Choose at least one client.'
    );
  }

  const scheduleKind =
    payload.scheduleKind ||
    payload.schedule_kind ||
    'day_of_month';

  const { data, error } = await db
    .from('aa_shared_task_anchors')
    .insert({
      task_name: taskName,
      task_type: taskType,
      schedule_kind: scheduleKind,
      day_of_month:
        ['day_of_month', 'yearly', 'quarterly'].includes(scheduleKind)
          ? Number(
              payload.dayOfMonth ||
              payload.day_of_month ||
              1
            )
          : null,
      month_of_year:
        ['yearly', 'yearly_flexible', 'quarterly', 'quarterly_flexible'].includes(scheduleKind)
          ? Number(payload.monthOfYear ?? payload.month_of_year ?? 1)
          : null,
      week_of_month:
        scheduleKind === 'nth_weekday'
          ? (
              payload.weekOfMonth ||
              payload.week_of_month ||
              'first'
            )
          : null,
      weekday:
        (
          scheduleKind === 'nth_weekday' ||
          scheduleKind === 'weekly'
        )
          ? (
              payload.weekday ||
              'Monday'
            )
          : null,
      use_client_owner:
        payload.useClientOwner !== false,
      assignee_team_member_id:
        payload.useClientOwner === false
          ? (
              payload.assigneeTeamMemberId ||
              payload.assignee_team_member_id ||
              null
            )
          : null,
      counts_toward_package:
        payload.countsTowardPackage !== false,
      package_service_catalog_id:
        payload.countsTowardPackage === false
          ? null
          : (
              payload.packageServiceCatalogId ||
              payload.package_service_catalog_id ||
              null
            ),
      notes:
        String(payload.notes || '').trim(),
      is_active:
        payload.isActive !== false,
      updated_at:
        new Date().toISOString()
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  const { error: assignmentError } =
    await db
      .from('aa_shared_task_anchor_clients')
      .insert(
        clientIds.map((clientId) => ({
          shared_anchor_id: data.id,
          client_id: clientId
        }))
      );

  if (assignmentError) {
    await db
      .from('aa_shared_task_anchors')
      .delete()
      .eq('id', data.id);

    throw assignmentError;
  }

  return data;
}

async function updateSharedTaskAnchor({
  id,
  updates = {},
  clientIds
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Shared Task Anchor is required.'
    );
  }

  const payload = {
    updated_at:
      new Date().toISOString()
  };

  const map = {
    taskName: 'task_name',
    taskType: 'task_type',
    scheduleKind: 'schedule_kind',
    dayOfMonth: 'day_of_month',
    monthOfYear: 'month_of_year',
    weekOfMonth: 'week_of_month',
    weekday: 'weekday',
    useClientOwner: 'use_client_owner',
    assigneeTeamMemberId:
      'assignee_team_member_id',
    countsTowardPackage:
      'counts_toward_package',
    packageServiceCatalogId:
      'package_service_catalog_id',
    notes: 'notes',
    isActive: 'is_active'
  };

  Object.entries(map).forEach(
    ([source, destination]) => {
      if (
        Object.prototype
          .hasOwnProperty
          .call(updates, source)
      ) {
        payload[destination] =
          updates[source];
      }
    }
  );

  if (
    updates.scheduleKind ===
      'day_of_month'
  ) {
    payload.week_of_month = null;
    payload.weekday = null;
  }

  if (
    updates.scheduleKind ===
      'nth_weekday'
  ) {
    payload.day_of_month = null;
  }

  if (
    updates.scheduleKind ===
      'weekly'
  ) {
    payload.day_of_month = null;
    payload.week_of_month = null;
  }

  if (
    updates.scheduleKind ===
      'monthly_flexible'
  ) {
    payload.day_of_month = null;
    payload.week_of_month = null;
    payload.weekday = null;
  }

  if (updates.scheduleKind && !['yearly', 'yearly_flexible', 'quarterly', 'quarterly_flexible'].includes(updates.scheduleKind)) {
    payload.month_of_year = null;
  }
  if (['yearly', 'yearly_flexible', 'quarterly', 'quarterly_flexible'].includes(updates.scheduleKind)) {
    payload.week_of_month = null;
    payload.weekday = null;
    if (updates.scheduleKind.endsWith('_flexible')) payload.day_of_month = null;
  }

  if (
    updates.useClientOwner === true
  ) {
    payload.assignee_team_member_id =
      null;
  }

  if (
    updates.countsTowardPackage === false
  ) {
    payload.package_service_catalog_id =
      null;
  }

  const { data, error } = await db
    .from('aa_shared_task_anchors')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  if (Array.isArray(clientIds)) {
    const uniqueClientIds =
      [...new Set(
        clientIds
          .map(String)
          .filter(Boolean)
      )];

    if (!uniqueClientIds.length) {
      throw new Error(
        'Choose at least one client.'
      );
    }

    const { error: deleteError } =
      await db
        .from('aa_shared_task_anchor_clients')
        .delete()
        .eq('shared_anchor_id', id);

    if (deleteError) {
      throw deleteError;
    }

    const { error: insertError } =
      await db
        .from('aa_shared_task_anchor_clients')
        .insert(
          uniqueClientIds.map(
            (clientId) => ({
              shared_anchor_id: id,
              client_id: clientId
            })
          )
        );

    if (insertError) {
      throw insertError;
    }
  }

  return data;
}

async function getTaskTypes() {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const { data, error } = await db
    .from('aa_task_types')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', {
      ascending: true
    })
    .order('task_type_name', {
      ascending: true
    });

  if (error) {
    throw error;
  }

  return (data || []).map(
    (item) => ({
      id: item.id,
      name: item.task_type_name,
      defaultScope:
        item.default_scope ||
        'client_deliverable',
      sortOrder:
        Number(item.sort_order || 0)
    })
  );
}

async function addTaskType({
  name,
  defaultScope =
    'client_deliverable'
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const taskTypeName =
    String(name || '').trim();

  if (!taskTypeName) {
    throw new Error(
      'Task type name is required.'
    );
  }

  const { data: existing } = await db
    .from('aa_task_types')
    .select('*')
    .ilike(
      'task_type_name',
      taskTypeName
    )
    .maybeSingle();

  if (existing?.id) {
    if (existing.is_active === false) {
      const { data, error } = await db
        .from('aa_task_types')
        .update({
          is_active: true,
          default_scope:
            defaultScope ||
            'client_deliverable',
          updated_at:
            new Date().toISOString()
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    }

    return existing;
  }

  const { data, error } = await db
    .from('aa_task_types')
    .insert({
      task_type_name:
        taskTypeName,
      default_scope:
        defaultScope ||
        'client_deliverable',
      sort_order: 500,
      updated_at:
        new Date().toISOString()
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function getTaskPackageServices({
  clientId
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  let query = db
    .from('aa_client_package_services')
    .select('*')
    .eq('is_active', true)
    .order('display_order', {
      ascending: true
    })
    .order('created_at', {
      ascending: true
    });

  if (clientId) {
    query = query.eq(
      'client_id',
      clientId
    );
  }

  const {
    data: packageRows,
    error: packageError
  } = await query;

  if (packageError) {
    throw packageError;
  }

  const serviceIds = [
    ...new Set(
      (packageRows || [])
        .map(
          (row) =>
            row.service_catalog_id
        )
        .filter(Boolean)
    )
  ];

  let catalogRows = [];

  if (serviceIds.length) {
    const {
      data,
      error
    } = await db
      .from('aa_service_catalog')
      .select('*')
      .in('id', serviceIds);

    if (error) {
      throw error;
    }

    catalogRows = data || [];
  }

  return (packageRows || []).map(
    (row) => {
      const service =
        catalogRows.find(
          (item) =>
            String(item.id) ===
            String(
              row.service_catalog_id
            )
        ) || {};

      return {
        id: row.id,
        clientId: row.client_id,
        serviceCatalogId:
          row.service_catalog_id,
        name:
          row.custom_name ||
          service.service_name ||
          'Service',
        category:
          service.category || '',
        quantity:
          Number(row.quantity || 0),
        unit:
          row.unit_label ||
          service.default_unit_label ||
          'per month',
        effectiveDate:
          row.effective_date || null,
        expirationDate:
          row.expiration_date || null,
        status:
          row.status || 'active'
      };
    }
  );
}


async function getQuoteServiceCatalog() {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const { data, error } = await db
    .from('aa_service_catalog')
    .select('*')
    .eq('quote_enabled', true)
    .order('sort_order', {
      ascending: true
    })
    .order('service_name', {
      ascending: true
    });

  if (error) {
    throw error;
  }

  return (data || []).map(
    (item) => ({
      id: item.id,
      name: item.service_name || '',
      description:
        item.quote_short_description ||
        item.service_description ||
        '',
      scope:
        item.quote_scope || '',
      defaultPrice:
        item.default_quote_price === null ||
        item.default_quote_price === undefined
          ? null
          : Number(
              item.default_quote_price
            ),
      pricingType:
        item.default_quote_pricing_type ||
        'custom',
      frequency:
        item.default_quote_frequency ||
        item.default_unit_label ||
        '',
      category:
        item.category || ''
    })
  );
}

async function getProspects({
  includeClosed = false
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  let query = db
    .from('aa_prospects')
    .select('*')
    .order('updated_at', {
      ascending: false
    });

  if (!includeClosed) {
    query = query.neq(
      'status',
      'closed'
    );
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data || [];
}

async function createProspect(
  payload = {}
) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const businessName =
    String(
      payload.businessName ??
      payload.business_name ??
      ''
    ).trim();

  if (!businessName) {
    throw new Error(
      'Prospect business name is required.'
    );
  }

  const { data, error } = await db
    .from('aa_prospects')
    .insert({
      business_name: businessName,
      contact_name:
        String(
          payload.contactName ??
          payload.contact_name ??
          ''
        ).trim() || null,
      email:
        String(
          payload.email || ''
        ).trim() || null,
      phone:
        String(
          payload.phone || ''
        ).trim() || null,
      notes:
        String(
          payload.notes || ''
        ).trim() || null,
      updated_at:
        new Date().toISOString()
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}


async function getContracts({
  includeArchived = false
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  let query = db
    .from('aa_contracts')
    .select('*')
    .order('updated_at', {
      ascending: false
    });

  if (!includeArchived) {
    query = query.is(
      'archived_at',
      null
    );
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data || [];
}


async function getQuotes({
  status,
  limit = 100
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  let query = db
    .from('aa_quotes')
    .select('*')
    .order('updated_at', {
      ascending: false
    })
    .limit(
      Math.max(
        1,
        Number(limit || 100)
      )
    );

  if (status) {
    query = query.eq(
      'status',
      status
    );
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data || [];
}

async function getQuote({
  id
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Quote is required.'
    );
  }

  const [
    quoteResult,
    itemResult,
    activityResult
  ] = await Promise.all([
    db
      .from('aa_quotes')
      .select('*')
      .eq('id', id)
      .single(),
    db
      .from('aa_quote_items')
      .select('*')
      .eq('quote_id', id)
      .order('sort_order', {
        ascending: true
      }),
    db
      .from('aa_quote_activity')
      .select('*')
      .eq('quote_id', id)
      .order('created_at', {
        ascending: false
      })
  ]);

  if (quoteResult.error) {
    throw quoteResult.error;
  }

  if (itemResult.error) {
    throw itemResult.error;
  }

  if (activityResult.error) {
    throw activityResult.error;
  }

  return {
    ...quoteResult.data,
    items: itemResult.data || [],
    activity:
      activityResult.data || []
  };
}

async function createQuote(
  payload = {}
) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const audienceType =
    String(
      payload.audienceType ??
      payload.audience_type ??
      ''
    ).trim();

  const clientId =
    payload.clientId ??
    payload.client_id ??
    null;

  const prospectId =
    payload.prospectId ??
    payload.prospect_id ??
    null;

  if (
    audienceType === 'client' &&
    !clientId
  ) {
    throw new Error(
      'Choose a client.'
    );
  }

  if (
    audienceType === 'prospect' &&
    !prospectId
  ) {
    throw new Error(
      'Choose or create a prospect.'
    );
  }

  const items =
    Array.isArray(payload.items)
      ? payload.items
      : [];

  if (!items.length) {
    throw new Error(
      'Add at least one service.'
    );
  }

  const normalizedItems =
    items.map(
      (item, index) => {
        const quantity =
          Math.max(
            Number(
              item.quantity ?? 1
            ),
            0
          );

        const unitPrice =
          Math.max(
            Number(
              item.unitPrice ??
              item.unit_price ??
              0
            ),
            0
          );

        return {
          service_catalog_id:
            item.serviceCatalogId ??
            item.service_catalog_id ??
            null,
          service_name:
            String(
              item.serviceName ??
              item.service_name ??
              'Service'
            ).trim() ||
            'Service',
          description:
            String(
              item.description || ''
            ).trim() || null,
          scope_text:
            String(
              item.scopeText ??
              item.scope_text ??
              ''
            ).trim() || null,
          frequency:
            String(
              item.frequency || ''
            ).trim() || null,
          pricing_type:
            String(
              item.pricingType ??
              item.pricing_type ??
              'custom'
            ).trim() ||
            'custom',
          quantity,
          unit_price: unitPrice,
          line_total:
            Number(
              (
                quantity *
                unitPrice
              ).toFixed(2)
            ),
          sort_order:
            Number(
              item.sortOrder ??
              item.sort_order ??
              index
            )
        };
      }
    );

  const monthlySubtotal =
    normalizedItems.reduce(
      (total, item) =>
        item.pricing_type === 'monthly'
          ? total +
            Number(
              item.line_total || 0
            )
          : total,
      0
    );

  const oneTimeTotal =
    normalizedItems.reduce(
      (total, item) =>
        item.pricing_type === 'one_time'
          ? total +
            Number(
              item.line_total || 0
            )
          : total,
      0
    );

  const { data: quote, error } =
    await db
      .from('aa_quotes')
      .insert({
        audience_type:
          audienceType,
        client_id:
          audienceType === 'client'
            ? clientId
            : null,
        prospect_id:
          audienceType === 'prospect'
            ? prospectId
            : null,
        quote_type:
          payload.quoteType ??
          payload.quote_type ??
          'custom',
        title:
          String(
            payload.title ||
            'Artisan\'s Avenue Quote'
          ).trim() ||
          'Artisan\'s Avenue Quote',
        status:
          payload.status ||
          'draft',
        issued_date:
          payload.issuedDate ??
          payload.issued_date ??
          null,
        valid_through:
          payload.validThrough ??
          payload.valid_through ??
          null,
        sender_team_member_id:
          payload.senderTeamMemberId ??
          payload.sender_team_member_id ??
          null,
        recipient_name:
          String(
            payload.recipientName ??
            payload.recipient_name ??
            ''
          ).trim() || null,
        recipient_email:
          String(
            payload.recipientEmail ??
            payload.recipient_email ??
            ''
          ).trim() || null,
        recipient_phone:
          String(
            payload.recipientPhone ??
            payload.recipient_phone ??
            ''
          ).trim() || null,
        intro_text:
          String(
            payload.introText ??
            payload.intro_text ??
            ''
          ).trim() || null,
        internal_notes:
          String(
            payload.internalNotes ??
            payload.internal_notes ??
            ''
          ).trim() || null,
        monthly_subtotal:
          Number(
            monthlySubtotal.toFixed(2)
          ),
        one_time_total:
          Number(
            oneTimeTotal.toFixed(2)
          ),
        updated_at:
          new Date().toISOString()
      })
      .select()
      .single();

  if (error) {
    throw error;
  }

  const {
    data: savedItems,
    error: itemError
  } = await db
    .from('aa_quote_items')
    .insert(
      normalizedItems.map(
        (item) => ({
          ...item,
          quote_id: quote.id
        })
      )
    )
    .select();

  if (itemError) {
    await db
      .from('aa_quotes')
      .delete()
      .eq('id', quote.id);

    throw itemError;
  }

  await db
    .from('aa_quote_activity')
    .insert({
      quote_id: quote.id,
      event_type: 'created',
      detail: {
        status: quote.status
      }
    });

  return {
    ...quote,
    items:
      savedItems || []
  };
}

async function updateQuote({
  id,
  updates = {}
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Quote is required.'
    );
  }

  const fieldMap = {
    status: 'status',
    title: 'title',
    quoteType: 'quote_type',
    issuedDate: 'issued_date',
    validThrough: 'valid_through',
    senderTeamMemberId:
      'sender_team_member_id',
    recipientName:
      'recipient_name',
    recipientEmail:
      'recipient_email',
    recipientPhone:
      'recipient_phone',
    introText: 'intro_text',
    internalNotes: 'internal_notes'
  };

  const record = {};

  Object.entries(
    fieldMap
  ).forEach(
    ([source, destination]) => {
      if (
        Object.prototype
          .hasOwnProperty
          .call(updates, source)
      ) {
        record[destination] =
          updates[source];
      }
    }
  );

  record.updated_at =
    new Date().toISOString();

  const { data, error } = await db
    .from('aa_quotes')
    .update(record)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  if (
    Object.prototype
      .hasOwnProperty
      .call(record, 'status')
  ) {
    await db
      .from('aa_quote_activity')
      .insert({
        quote_id: id,
        event_type:
          'status_changed',
        detail: {
          status: record.status
        }
      });
  }

  return data;
}


async function getTeamTaskSummary({
  month
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const normalizedMonth =
    month
      ? (
          /^\d{4}-\d{2}$/.test(
            String(month)
          )
            ? `${month}-01`
            : String(month)
        )
      : null;

  const { data, error } = await db.rpc(
    'aa_get_team_task_summary',
    {
      p_month:
        normalizedMonth || null
    }
  );

  if (error) {
    throw error;
  }

  return (data || []).map(
    (row) => ({
      teamMemberId:
        row.team_member_id,
      teamMemberName:
        row.team_member_name ||
        'Team Member',
      clientsOwned:
        Number(
          row.clients_owned || 0
        ),
      total:
        Number(
          row.total_tasks || 0
        ),
      notStarted:
        Number(
          row.not_started_tasks || 0
        ),
      inProgress:
        Number(
          row.in_progress_tasks || 0
        ),
      waiting:
        Number(
          row.waiting_tasks || 0
        ),
      issue:
        Number(
          row.issue_tasks || 0
        ),
      completed:
        Number(
          row.completed_tasks || 0
        ),
      active:
        Number(
          row.active_tasks || 0
        ),
      overdue:
        Number(
          row.overdue_tasks || 0
        ),
      completionPercent:
        Number(
          row.completion_percent || 0
        )
    })
  );
}

async function getOperationsAttention() {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const { data, error } =
    await db.rpc(
      'aa_get_operations_attention'
    );

  if (error) {
    throw error;
  }

  return data || {
    clientCount: 0,
    billingLinked: 0,
    billingUnlinked: 0,
    missingContractCount: 0,
    contractAttentionCount: 0,
    packageRequestCount: 0,
    accessIssueCount: 0,
    blockedTaskCount: 0,
    missingContracts: [],
    contractAttention: [],
    packageRequests: [],
    accessIssues: [],
    blockedTasks: []
  };
}

async function getOperationsPackageSummary({
  month
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const normalizedMonth =
    month
      ? (
          /^\d{4}-\d{2}$/.test(
            String(month)
          )
            ? `${month}-01`
            : String(month)
        )
      : null;

  const { data, error } = await db.rpc(
    'aa_get_operations_package_summary',
    {
      p_month:
        normalizedMonth || null
    }
  );

  if (error) {
    throw error;
  }

  return (data || []).map(
    (row) => ({
      clientId:
        row.client_id,
      clientCode:
        row.client_code || '',
      clientName:
        row.client_name || '',
      ownerName:
        row.owner_name || '',
      packageServiceId:
        row.package_service_id,
      serviceCatalogId:
        row.service_catalog_id,
      serviceName:
        row.service_name || '',
      unitLabel:
        row.unit_label || '',
      packageQuantity:
        Number(
          row.package_quantity || 0
        ),
      contracted:
        Number(
          row.contracted_units || 0
        ),
      scheduled:
        Number(
          row.scheduled_units || 0
        ),
      completed:
        Number(
          row.completed_units || 0
        ),
      unscheduled:
        Number(
          row.unscheduled_units || 0
        ),
      stillToComplete:
        Number(
          row.still_to_complete_units ||
          0
        ),
      variance:
        Number(
          row.variance_units || 0
        )
    })
  );
}

async function getTaskGeneratorSettings() {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const { data, error } = await db
    .from('aa_task_generator_settings')
    .select('*')
    .eq('settings_key', 'global')
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data || {
    settings_key: 'global',
    instructions: ''
  };
}

async function saveTaskGeneratorSettings({
  instructions
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const value =
    String(instructions || '').trim();

  if (!value) {
    throw new Error(
      'Task Generator Instructions cannot be empty.'
    );
  }

  const {
    data: authData,
    error: authError
  } = await db.auth.getUser();

  if (authError) {
    throw authError;
  }

  const userId =
    authData?.user?.id || null;

  const { data, error } = await db
    .from('aa_task_generator_settings')
    .upsert(
      {
        settings_key: 'global',
        instructions: value,
        updated_by: userId,
        updated_at:
          new Date().toISOString()
      },
      {
        onConflict: 'settings_key'
      }
    )
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function getTaskGeneratorStatus() {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  const { data, error } =
    await db.functions.invoke(
      'aa-task-generator-weekdays',
      {
        body: {
          action: 'status'
        }
      }
    );

  if (error) {
    let message =
      error?.message ||
      'Unable to check Task Generator status.';

    try {
      const payload =
        await error.context?.json?.();

      if (payload?.error) {
        message = payload.error;
      }
    } catch {
      // Keep the original function error.
    }

    throw new Error(message);
  }

  if (!data?.success) {
    throw new Error(
      data?.error ||
      'Unable to check Task Generator status.'
    );
  }

  return data.data || {};
}

async function invokeTaskGenerator({
  catalogId,
  monthlyNotes = ''
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!catalogId) {
    throw new Error(
      'Catalog is required.'
    );
  }

  const invokeAction =
    async (body) => {
      const { data, error } =
        await db.functions.invoke(
          'aa-task-generator-weekdays',
          { body }
        );

      if (error) {
        let message =
          error?.message ||
          'Unable to generate tasks.';

        let code = '';

        try {
          const payload =
            await error.context
              ?.json?.();

          if (payload?.error) {
            message =
              payload.error;
          }

          if (payload?.code) {
            code =
              payload.code;
          }
        } catch {
          // Keep the original function error.
        }

        const wrapped =
          new Error(message);

        wrapped.code =
          code ||
          error?.name ||
          'TASK_GENERATION_FAILED';

        throw wrapped;
      }

      if (!data?.success) {
        const wrapped =
          new Error(
            data?.error ||
            'Unable to generate tasks.'
          );

        wrapped.code =
          data?.code ||
          'TASK_GENERATION_FAILED';

        throw wrapped;
      }

      return (
        data.data?.batch ||
        null
      );
    };

  let batch =
    await invokeAction({
      action: 'generate',
      catalogId,
      monthlyNotes:
        String(
          monthlyNotes ||
          ''
        ).trim()
    });

  if (!batch?.id) {
    return batch;
  }

  const maxPolls = 135;

  for (
    let attempt = 0;
    batch?.status ===
      'generating' &&
    attempt < maxPolls;
    attempt += 1
  ) {
    await new Promise(
      (resolve) =>
        window.setTimeout(
          resolve,
          4000
        )
    );

    batch =
      await invokeAction({
        action: 'poll',
        batchId: batch.id
      });
  }

  if (
    batch?.status ===
    'generating'
  ) {
    const error =
      new Error(
        'Bea is still building this proposal. Please try opening Generate Tasks again in a moment.'
      );

    error.code =
      'TASK_GENERATION_TIMEOUT';

    throw error;
  }

  if (
    batch?.status ===
    'failed'
  ) {
    const error =
      new Error(
        'The proposal did not finish successfully. Please generate again.'
      );

    error.code =
      'TASK_GENERATION_FAILED';

    throw error;
  }

  return batch;
}

async function pollTaskGeneratorBatch({
  batchId
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!batchId) {
    throw new Error(
      'Generation batch is required.'
    );
  }

  const { data, error } =
    await db.functions.invoke(
      'aa-task-generator-weekdays',
      {
        body: {
          action: 'poll',
          batchId
        }
      }
    );

  if (error) {
    let message =
      error?.message ||
      'Unable to check Leo status.';

    let code = '';

    try {
      const payload =
        await error.context?.json?.();

      if (payload?.error) {
        message = payload.error;
      }

      if (payload?.code) {
        code = payload.code;
      }
    } catch {
      // Keep the original function error.
    }

    const wrapped =
      new Error(message);

    wrapped.code =
      code ||
      error?.name ||
      'TASK_GENERATION_POLL_FAILED';

    throw wrapped;
  }

  if (!data?.success) {
    const wrapped =
      new Error(
        data?.error ||
        'Unable to check Leo status.'
      );

    wrapped.code =
      data?.code ||
      'TASK_GENERATION_POLL_FAILED';

    throw wrapped;
  }

  return data.data?.batch || null;
}

async function getTaskGenerationBatches({
  catalogId,
  clientId,
  includeSuperseded = false
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  let query = db
    .from('aa_task_generation_batches')
    .select('*')
    .order('created_at', {
      ascending: false
    });

  if (catalogId) {
    query = query.eq(
      'catalog_id',
      catalogId
    );
  }

  if (clientId) {
    query = query.eq(
      'client_id',
      clientId
    );
  }

  if (!includeSuperseded) {
    query = query.neq(
      'status',
      'superseded'
    );
  }

  const { data, error } =
    await query;

  if (error) {
    throw error;
  }

  return data || [];
}

async function getLatestTaskGenerationBatch({
  catalogId
} = {}) {
  if (!catalogId) {
    return null;
  }

  const rows =
    await getTaskGenerationBatches({
      catalogId
    });

  return rows[0] || null;
}

async function getTaskGenerationBatch({
  id
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Generation batch is required.'
    );
  }

  const [
    batchResult,
    itemsResult
  ] = await Promise.all([
    db
      .from('aa_task_generation_batches')
      .select('*')
      .eq('id', id)
      .single(),
    db
      .from('aa_task_generation_items')
      .select('*')
      .eq('batch_id', id)
      .order('sort_order', {
        ascending: true
      })
  ]);

  if (batchResult.error) {
    throw batchResult.error;
  }

  if (itemsResult.error) {
    throw itemsResult.error;
  }

  return {
    ...batchResult.data,
    items:
      itemsResult.data || []
  };
}

async function updateTaskGenerationBatch({
  id,
  updates = {}
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Generation batch is required.'
    );
  }

  const payload = {
    updated_at:
      new Date().toISOString()
  };

  if (
    Object.prototype
      .hasOwnProperty
      .call(updates, 'monthlyNotes')
  ) {
    payload.monthly_notes =
      String(
        updates.monthlyNotes || ''
      );
  }

  if (
    Object.prototype
      .hasOwnProperty
      .call(updates, 'monthly_notes')
  ) {
    payload.monthly_notes =
      String(
        updates.monthly_notes || ''
      );
  }

  const { data, error } = await db
    .from('aa_task_generation_batches')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function updateTaskGenerationItem({
  id,
  updates = {}
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!id) {
    throw new Error(
      'Generated task is required.'
    );
  }

  const payload = {
    updated_at:
      new Date().toISOString()
  };

  const map = {
    specificItem: 'specific_item',
    dueDate: 'due_date',
    notes: 'notes',
    included: 'included'
  };

  Object.entries(map).forEach(
    ([source, destination]) => {
      if (
        Object.prototype
          .hasOwnProperty
          .call(updates, source)
      ) {
        payload[destination] =
          updates[source];
      }
    }
  );

  [
    'specific_item',
    'due_date',
    'notes',
    'included'
  ].forEach((field) => {
    if (
      Object.prototype
        .hasOwnProperty
        .call(updates, field)
    ) {
      payload[field] =
        updates[field];
    }
  });

  const { data, error } = await db
    .from('aa_task_generation_items')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function approveTaskGenerationBatch({
  batchId
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!batchId) {
    throw new Error(
      'Generation batch is required.'
    );
  }

  const { data, error } = await db
    .rpc(
      'aa_approve_task_generation_batch',
      {
        p_batch_id: batchId
      }
    );

  if (error) {
    throw error;
  }

  return Number(data || 0);
}

async function undoTaskGenerationBatch({
  batchId
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error(
      'Supabase client is unavailable.'
    );
  }

  if (!batchId) {
    throw new Error(
      'Generation batch is required.'
    );
  }

  const { data, error } = await db
    .rpc(
      'aa_undo_task_generation_batch',
      {
        p_batch_id: batchId
      }
    );

  if (error) {
    throw error;
  }

  return Number(data || 0);
}

async function getTasks({
  assigneeTeamMemberId,
  clientId,
  includeArchived = false
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  let query = db
    .from('aa_tasks')
    .select('*')
    .order('due_date', {
      ascending: true,
      nullsFirst: false
    })
    .order('created_at', {
      ascending: true
    });

  if (assigneeTeamMemberId) {
    query = query.eq(
      'assignee_team_member_id',
      assigneeTeamMemberId
    );
  }

  if (clientId) {
    query = query.eq(
      'client_id',
      clientId
    );
  }

  if (!includeArchived) {
    query = query.neq(
      'status',
      'Archived'
    );
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data || [];
}

async function createTask(payload = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  const taskType =
    String(
      payload.taskType ??
      payload.task_type ??
      ''
    ).trim();

  const specificItem =
    String(
      payload.specificItem ??
      payload.specific_item ??
      ''
    ).trim();

  if (!taskType) {
    throw new Error('Task type is required.');
  }

  if (!specificItem) {
    throw new Error('Specific item is required.');
  }

  let assigneeTeamMemberId =
    payload.assigneeTeamMemberId ??
    payload.assignee_team_member_id ??
    null;

  const clientId =
    payload.clientId ??
    payload.client_id ??
    null;

  if (!assigneeTeamMemberId && clientId) {
    const { data: clientRow } = await db
      .from('aa_clients')
      .select('primary_team_member_id')
      .eq('id', clientId)
      .maybeSingle();

    assigneeTeamMemberId =
      clientRow?.primary_team_member_id ||
      null;
  }

  const record = {
    client_id: clientId || null,
    task_type: taskType,
    specific_item: specificItem,
    assignee_team_member_id:
      assigneeTeamMemberId || null,
    status:
      String(payload.status || 'Not Started').trim() ||
      'Not Started',
    due_date:
      payload.dueDate ??
      payload.due_date ??
      null,
    notes:
      payload.notes === undefined ||
      payload.notes === null ||
      String(payload.notes).trim() === ''
        ? null
        : String(payload.notes).trim(),
    origin:
      String(
        payload.origin || 'manual'
      ).trim() || 'manual',
    source_type:
      payload.sourceType ??
      payload.source_type ??
      null,
    source_id:
      payload.sourceId ??
      payload.source_id ??
      null,
    task_scope:
      String(
        payload.taskScope ??
        payload.task_scope ??
        (
          clientId
            ? 'client_deliverable'
            : 'internal_admin'
        )
      ).trim() ||
      (
        clientId
          ? 'client_deliverable'
          : 'internal_admin'
      ),
    counts_toward_package:
      payload.countsTowardPackage ??
      payload.counts_toward_package ??
      false,
    package_service_id:
      payload.packageServiceId ??
      payload.package_service_id ??
      null,
    package_units:
      Math.max(
        Number(
          payload.packageUnits ??
          payload.package_units ??
          1
        ),
        0.01
      ),
    updated_at:
      new Date().toISOString()
  };

  const { data, error } = await db
    .from('aa_tasks')
    .insert(record)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function updateTask({
  id,
  updates = {}
} = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!id) {
    throw new Error('Task id is required.');
  }

  const payload = {};

  const fieldMap = {
    clientId: 'client_id',
    taskType: 'task_type',
    specificItem: 'specific_item',
    assigneeTeamMemberId:
      'assignee_team_member_id',
    dueDate: 'due_date',
    sourceType: 'source_type',
    sourceId: 'source_id',
    taskScope: 'task_scope',
    countsTowardPackage:
      'counts_toward_package',
    packageServiceId:
      'package_service_id',
    packageUnits:
      'package_units'
  };

  Object.entries(fieldMap).forEach(
    ([source, destination]) => {
      if (
        Object.prototype.hasOwnProperty.call(
          updates,
          source
        )
      ) {
        payload[destination] =
          updates[source];
      }
    }
  );

  [
    'client_id',
    'task_type',
    'specific_item',
    'assignee_team_member_id',
    'status',
    'due_date',
    'notes',
    'origin',
    'source_type',
    'source_id',
    'task_scope',
    'counts_toward_package',
    'package_service_id',
    'package_units',
    'completed_at'
  ].forEach((field) => {
    if (
      Object.prototype.hasOwnProperty.call(
        updates,
        field
      )
    ) {
      payload[field] = updates[field];
    }
  });

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      'status'
    )
  ) {
    const status =
      String(payload.status || '').trim();

    payload.status =
      status || 'Not Started';

    if (
      payload.status === 'Completed' &&
      !Object.prototype.hasOwnProperty.call(
        payload,
        'completed_at'
      )
    ) {
      payload.completed_at =
        new Date().toISOString();
    }

    if (
      payload.status !== 'Completed' &&
      !Object.prototype.hasOwnProperty.call(
        updates,
        'completed_at'
      )
    ) {
      payload.completed_at = null;
    }
  }

  payload.updated_at =
    new Date().toISOString();

  const { data, error } = await db
    .from('aa_tasks')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

async function archiveTask({
  id
} = {}) {
  return updateTask({
    id,
    updates: {
      status: 'Archived'
    }
  });
}

async function invokeSignwellAction(action, extra = {}) {
  const db = getClient();
  if (!db) throw new Error('Supabase client is unavailable.');
  const { data, error } = await db.functions.invoke('aa-signwell-service', {
    body: { action, ...extra }
  });
  if (error) {
    let message = error.message || 'Unable to reach SignWell.';
    try {
      const details = await error.context?.json?.();
      message = details?.error || message;
    } catch (_) {}
    throw new Error(message);
  }
  if (!data?.success) throw new Error(data?.error || 'SignWell request could not be completed.');
  return data.data;
}


async function invokeSignwellWorkflow(action, extra = {}) {
  const db = getClient();
  if (!db) throw new Error('Supabase is unavailable.');
  const {data,error} = await db.functions.invoke('aa-signwell-workflow', {
    body:{action,...extra}
  });
  if (error) {
    let message = error.message || 'Unable to load agreements.';
    try {
      const details = await error.context?.json?.();
      message = details?.error || message;
    } catch (_) {}
    throw new Error(message);
  }
  if (!data?.success) throw new Error(data?.error || 'Agreement action failed.');
  return data.data;
}

async function getSignwellLiveAgreements() {
  return invokeSignwellWorkflow('list-live');
}

async function runSignwellLiveAction(action, id, extra = {}) {
  return invokeSignwellWorkflow(action, {id,...extra});
}

async function prepareSignwellLiveDraft({
  clientId,templateType,recipients,agreementDetails,serviceSchedule,packageChangeRequestId
} = {}) {
  return invokeSignwellAction('prepare-live-draft', {
    clientId,templateType,recipients,agreementDetails,serviceSchedule,
    packageChangeRequestId,liveDraftConfirmation:'PREPARE LIVE DRAFT'
  });
}

async function getApprovedAgreementPackageChanges() {
  const db = getClient();
  if (!db) throw new Error('Supabase unavailable.');
  const {data,error} = await db.from('aa_package_change_requests')
    .select('id,client_id,service_name,requested_package_value,requested_at,request_status')
    .in('request_status',['approved','implemented'])
    .order('requested_at',{ascending:false})
    .limit(150);
  if (error) throw error;
  return data || [];
}

async function getSignwellDefaultSigners() {
  return invokeSignwellAction('get-default-signers');
}

async function getSignwellTestDraftLink(id) {
  return invokeSignwellAction('get-test-draft-link', { id });
}

async function deleteSignwellTestDraft(id) {
  return invokeSignwellAction('delete-test-draft', { id });
}

async function getSignwellTestDrafts() {
  return invokeSignwellAction('list-test-drafts');
}

async function prepareSignwellTestDraft({
  clientId, templateType, recipients, agreementDetails, serviceSchedule
} = {}) {
  // Preserve the reviewed contract data. Sending only recipients loses
  // addresses, effective dates, package price and services at the API boundary.
  return invokeSignwellAction('prepare-test-draft', {
    clientId, templateType, recipients, agreementDetails, serviceSchedule
  });
}

async function updateAgreementServiceLibraryItem({
  serviceId, serviceName, category, defaultUnitLabel, description
} = {}) {
  const db = getClient();
  if (!db) throw new Error('Supabase client is unavailable.');
  const { data, error } = await db.rpc('aa_update_service_catalog_item', {
    p_service_id: serviceId,
    p_service_name: String(serviceName || '').trim(),
    p_category: String(category || '').trim() || null,
    p_default_unit_label: String(defaultUnitLabel || 'per month').trim(),
    p_service_description: String(description || '').trim() || null
  });
  if (error) throw new Error(error.message || 'Unable to update service.');
  if (data?.success !== true) throw new Error('Service update was not saved.');
  return data;
}

async function autoConfigureSignwellPrefill({ templateType = 'service_agreement' } = {}) {
  // Reconnect only the requested agreement. Do not change the addendum
  // while the annual template is being finalized.
  return invokeSignwellAction('auto-configure-prefill', { templateType });
}

async function saveSignwellPrefillMapping({templateType,mapping}={}) {
  return invokeSignwellAction('save-prefill-mapping', {templateType,mapping});
}

async function getSignwellConfiguration() {
  const db = getClient();
  if (!db) throw new Error('Supabase client is unavailable.');
  const { data, error } = await db.functions.invoke('aa-signwell-service', {
    body: { action: 'get-configuration' }
  });
  if (error) throw new Error('Unable to load SignWell settings.');
  if (!data?.success) throw new Error(data?.error || 'Unable to load SignWell settings.');
  return data.data;
}

async function verifySignwellConfiguredTemplates() {
  const db = getClient();
  if (!db) throw new Error('Supabase client is unavailable.');
  const { data, error } = await db.functions.invoke('aa-signwell-service', {
    body: { action: 'verify-configured-templates' }
  });
  if (error) {
    let message = error.message || 'Unable to verify SignWell templates.';
    try {
      const payload = await error.context?.json?.();
      message = payload?.error || message;
    } catch (_) {}
    throw new Error(message);
  }
  if (!data?.success) throw new Error(data?.error || 'SignWell template verification failed.');
  return data.data;
}

async function testSignwellConnection() {
  const db = getClient();
  if (!db) throw new Error('Supabase client is unavailable.');

  const { data, error } = await db.functions.invoke(
    'aa-signwell-service',
    { body: { action: 'test-connection' } }
  );

  if (error) {
    let message = error.message || 'Unable to contact SignWell.';
    try {
      const payload = await error.context?.json?.();
      message = payload?.error || message;
    } catch (_) {}
    throw new Error(message);
  }
  if (!data?.success || data?.data?.connected !== true) {
    throw new Error(data?.error || 'SignWell API connection could not be verified.');
  }
  return data.data;
}

async function getSignwellTemplate(templateId) {
  const db = getClient();
  if (!db) throw new Error('Supabase client is unavailable.');
  const { data, error } = await db.functions.invoke(
    'aa-signwell-service',
    { body: { action: 'get-template', templateId } }
  );

  if (error) {
    let message = error.message || 'Unable to verify the SignWell template.';
    try {
      const payload = await error.context?.json?.();
      message = payload?.error || message;
    } catch (_) {}
    throw new Error(message);
  }
  if (!data?.success) {
    throw new Error(data?.error || 'Unable to verify the SignWell template.');
  }
  return data.data;
}

async function getClientTemplateAssignments({ clientId } = {}) {
  const db = getClient();

  if (!db) {
    throw new Error('Supabase client is unavailable.');
  }

  if (!clientId) {
    return [];
  }

  const { data: assignments, error: assignmentError } = await db
    .from('aa_client_template_assignments')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });

  if (assignmentError) {
    throw assignmentError;
  }

  if (!assignments?.length) {
    return [];
  }

  const templateIds = [
    ...new Set(
      assignments
        .map((assignment) => assignment.template_id)
        .filter(Boolean)
    )
  ];

  let templates = [];

  if (templateIds.length) {
    const { data, error } = await db
      .from('aa_workspace_templates')
      .select('*')
      .in('id', templateIds);

    if (error) {
      throw error;
    }

    templates = data || [];
  }

  return assignments.map((assignment) => {
    const template =
      templates.find(
        (item) => String(item.id) === String(assignment.template_id)
      ) || {};

    return {
      id: assignment.id,
      templateId: assignment.template_id,
      templateName:
        template.template_name ||
        template.name ||
        template.title ||
        'Unnamed Template',
      templateVersion:
        template.template_version ||
        template.version ||
        'v1',
      assignmentStatus:
        assignment.assignment_status ||
        assignment.status ||
        'Assigned'
    };
  });
}

return {
  init,
  getClient,
  getClients,
  createClient,
  updateClient,
  archiveClient,
  supportsClientCodes,
  supportsClientAssignments,
  getInternalTeamMembers,
  getClientPrimaryAssignments,
  updateClientPrimaryTeamMember,
  getClientConfiguration,
  saveClientConfiguration,
  getClientAccount,

  uploadCatalogCover,
  uploadCatalogPdf,
  getCatalogPdfPublicUrl,
  getCatalogPdfSignedUrl,
  deleteCatalogPdf,
  uploadPlaybookFile,
  getPlaybookFileSignedUrl,
  deletePlaybookFile,

  getCatalogRecords,
  getCatalogRecord,
  createCatalogRecord,
  updateCatalogRecord,
  archiveCatalogRecord,
  canPermanentlyDeleteCatalogs,
  previewCatalogDeletion,
  permanentlyDeleteCatalogs,
  getCoopReports,
  createCoopReport,
  archiveCoopReport,
  deleteCoopReport,
  getPreviousMonthCatalog,

  getEmailRequests,
  getEmailRequest,
  createEmailRequest,
  updateEmailRequest,
  archiveEmailRequest,
  deleteEmailRequest,
  getEmailRequestFiles,
  uploadEmailRequestFile,
  getEmailRequestFileSignedUrl,
  removeEmailRequestFile,

  supportsTasks,
  getClientProductionSettings,
  saveClientProductionSettings,
  getClientTaskAnchors,
  createClientTaskAnchor,
  updateClientTaskAnchor,
  deleteClientTaskAnchor,
  getSharedTaskAnchors,
  createSharedTaskAnchor,
  updateSharedTaskAnchor,
  getTaskTypes,
  addTaskType,
  getTaskPackageServices,
  getQuoteServiceCatalog,
  getProspects,
  createProspect,
  getContracts,
  getQuotes,
  getQuote,
  createQuote,
  updateQuote,
  getTeamTaskSummary,
  getOperationsAttention,
  getOperationsPackageSummary,
  getTaskGeneratorSettings,
  saveTaskGeneratorSettings,
  getTaskGeneratorStatus,
  invokeTaskGenerator,
  pollTaskGeneratorBatch,
  getTaskGenerationBatches,
  getLatestTaskGenerationBatch,
  getTaskGenerationBatch,
  updateTaskGenerationBatch,
  updateTaskGenerationItem,
  approveTaskGenerationBatch,
  undoTaskGenerationBatch,
  getTasks,
  createTask,
  updateTask,
  archiveTask,

  updateAgreementServiceLibraryItem,
  autoConfigureSignwellPrefill,
  saveSignwellPrefillMapping,
  getSignwellConfiguration,
  verifySignwellConfiguredTemplates,
  getSignwellLiveAgreements,
  runSignwellLiveAction,
  prepareSignwellLiveDraft,
  getApprovedAgreementPackageChanges,
  getSignwellDefaultSigners,
  getSignwellTestDrafts,
  getSignwellTestDraftLink,
  deleteSignwellTestDraft,
  prepareSignwellTestDraft,
  testSignwellConnection,
  getSignwellTemplate,
  getClientTemplateAssignments,
  updateClientTemplateAssignmentStatus,
  deleteClientTemplateAssignment
};
})();